import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { inventoryApi, cartApi, adminConfigApi } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/common/Button'
import Input from '../components/common/Input'

/**
 * Shop — card-grid catalog browser for non-admins.
 *
 * Layout:
 *   - Items grouped under category headings
 *   - Category order respects the admin-configured categorySortOrder
 *     from admin_config; categories not in that list fall to the bottom,
 *     alphabetical among themselves
 *   - Each card shows: image, optional 'new' badge, description, item
 *     number/variant, price, stock, quantity input, Add/Backorder button
 *   - Out-of-stock items are dimmed but still visible, button reads "Backorder"
 *
 * Medallion variant grouping:
 *   - The "Medallions" category renders ONE card per medallion_type
 *     (Bronze, Triplate, Laser-Etched, Eternity, Accessory)
 *   - Inside each medallion card, a Year dropdown picks a variant
 *   - Triplate cards also have a Color dropdown
 *   - Stock + price + Add button reflect the currently-selected variant
 *
 * Deep-linking from the Dashboard:
 *   - The Dashboard's Featured Items banner sends users here with
 *     /shop?focus=<inventoryId>. We scroll to the matching card and
 *     briefly flash a highlight ring so they can see what was selected.
 *     For medallion variants, the card's year/color picker is pre-set
 *     to that specific variant so "Add" picks the right SKU.
 */
const MEDALLION_CATEGORY = 'Medallions'

const GROUPED_MEDALLION_TYPES = new Set(['Bronze', 'Triplate', 'Laser-Etched', 'Eternity'])

export default function Shop() {
    const { user } = useAuth()
    const noCustomer = !user?.customerId

    const [items, setItems] = useState([])
    const [categories, setCategories] = useState([])
    const [loading, setLoading] = useState(true)
    const [categorySortOrder, setCategorySortOrder] = useState(null)

    const [filterCategory, setFilterCategory] = useState('')
    const [searchText, setSearchText] = useState('')
    const [filterNewOnly, setFilterNewOnly] = useState(false)

    const [quantities, setQuantities] = useState({})
    const [selectedVariant, setSelectedVariant] = useState({})
    const [addingId, setAddingId] = useState(null)

    // Deep-link target from ?focus=<id>. Cleared once we've scrolled/flashed
    // so leaving the page and coming back doesn't keep re-flashing.
    const [searchParams, setSearchParams] = useSearchParams()
    const focusId = searchParams.get('focus')
    const focusedInventoryId = focusId ? Number(focusId) : null
    const [highlightedKey, setHighlightedKey] = useState(null)
    const cardRefs = useRef({})

    useEffect(() => {
        loadCategories()
        loadPublicConfig()
    }, [])
    useEffect(() => { loadItems() }, [filterCategory, searchText, filterNewOnly])
    useEffect(() => {
        function handleCartChanged() {
            loadItems()
        }
        window.addEventListener('ghasc:cart-changed', handleCartChanged)
        return () => window.removeEventListener('ghasc:cart-changed', handleCartChanged)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterCategory, searchText, filterNewOnly])

    async function loadItems() {
        setLoading(true)
        try {
            const filters = {
                category: filterCategory || undefined,
                q: searchText.trim() || undefined,
                isNew: filterNewOnly ? true : undefined,
            }
            const response = await inventoryApi.listShop(filters)
            if (response.success) setItems(response.items)
            else toast.error(response.message || 'Could not load items')
        } catch {
            toast.error('Network error loading catalog')
        } finally {
            setLoading(false)
        }
    }

    async function loadCategories() {
        try {
            const response = await inventoryApi.listCategories()
            if (response.success) setCategories(response.categories)
        } catch { /* non-fatal */ }
    }

    async function loadPublicConfig() {
        try {
            const response = await adminConfigApi.getPublic()
            if (response.success) {
                setCategorySortOrder(response.config?.categorySortOrder || null)
            }
        } catch { /* non-fatal */ }
    }

    // ----- No-customer state -------------------------------------------------
    if (noCustomer) {
        return (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center max-w-xl mx-auto">
                <h2 className="text-xl font-semibold text-ghasc-navy">Account not linked yet</h2>
                <p className="text-gray-600 mt-2 text-sm">
                    Your account isn't linked to a homegroup or subcommittee yet, so you
                    can't place orders. Reach out to the literature chair to be linked.
                </p>
            </div>
        )
    }

    // ----- Build grouped + variant structure --------------------------------
    const groupedByCategory = useMemo(() => {
        const byCategory = {}

        for (const item of items) {
            const cat = item.category || 'Uncategorized'
            if (!byCategory[cat]) byCategory[cat] = {}

            let groupKey
            if (cat === MEDALLION_CATEGORY && GROUPED_MEDALLION_TYPES.has(item.medallionType)) {
                groupKey = `medallion:${item.medallionType}`
            } else {
                groupKey = `item:${item.id}`
            }

            if (!byCategory[cat][groupKey]) {
                byCategory[cat][groupKey] = {
                    key: groupKey,
                    type: cat === MEDALLION_CATEGORY && item.medallionType ? 'medallion' : 'item',
                    medallionType: item.medallionType || null,
                    variants: [],
                }
            }
            byCategory[cat][groupKey].variants.push(item)
        }

        return byCategory
    }, [items])

    // Category ordering — respect admin-configured order, else alphabetical.
    // Categories in the order list appear in that order. Categories not in
    // the list fall to the end alphabetically.
    const sortedCategoryNames = useMemo(() => {
        const allCats = Object.keys(groupedByCategory)
        if (!categorySortOrder || categorySortOrder.length === 0) {
            return [...allCats].sort((a, b) => a.localeCompare(b))
        }
        const orderedSet = new Set(categorySortOrder)
        const ordered = categorySortOrder.filter(c => allCats.includes(c))
        const rest = allCats.filter(c => !orderedSet.has(c)).sort((a, b) => a.localeCompare(b))
        return [...ordered, ...rest]
    }, [groupedByCategory, categorySortOrder])

    // Given a focus inventory id, work out which card key contains it AND,
    // if it's a medallion variant, the specific variant inventory id to pre-select.
    const focusedTarget = useMemo(() => {
        if (!focusedInventoryId || items.length === 0) return null
        const item = items.find(i => i.id === focusedInventoryId)
        if (!item) return null

        const cat = item.category || 'Uncategorized'
        const isGroupedMedallion = cat === MEDALLION_CATEGORY
            && GROUPED_MEDALLION_TYPES.has(item.medallionType)

        const groupKey = isGroupedMedallion
            ? `medallion:${item.medallionType}`
            : `item:${item.id}`

        return { groupKey, variantId: item.id, isMedallion: isGroupedMedallion }
    }, [focusedInventoryId, items])

    // Once items are loaded and we know the focused card, scroll to it and
    // flash a highlight. We do this only once per ?focus= value — clearing
    // the query string afterward so re-renders don't keep triggering it.
    useEffect(() => {
        if (loading) return
        if (!focusedTarget) return

        // Pre-select the right variant on a medallion card so the user lands
        // looking at the SKU they clicked from the dashboard.
        if (focusedTarget.isMedallion && focusedTarget.variantId) {
            setSelectedVariant(s => ({
                ...s,
                [focusedTarget.groupKey]: focusedTarget.variantId,
            }))
        }

        // Wait one tick so the DOM has rendered the card.
        const timer = setTimeout(() => {
            const el = cardRefs.current[focusedTarget.groupKey]
            if (el && typeof el.scrollIntoView === 'function') {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
            setHighlightedKey(focusedTarget.groupKey)

            // Clear the ?focus= param so a refresh doesn't re-trigger.
            const next = new URLSearchParams(searchParams)
            next.delete('focus')
            setSearchParams(next, { replace: true })

            // Drop the highlight after 2.5s.
            setTimeout(() => setHighlightedKey(null), 2500)
        }, 50)

        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, focusedTarget?.groupKey, focusedTarget?.variantId])

    function getQuantity(key) {
        return quantities[key] ?? '0'
    }

    function setQuantity(key, value) {
        setQuantities(q => ({ ...q, [key]: String(value) }))
    }

    function commitQuantity(key) {
        const raw = quantities[key] ?? '0'
        const n = parseInt(raw, 10)
        const clean = (isNaN(n) || n < 0) ? 0 : n
        setQuantities(q => ({ ...q, [key]: String(clean) }))
    }

    function stepQuantity(key, delta) {
        const raw = quantities[key] ?? '0'
        const n = parseInt(raw, 10)
        const current = isNaN(n) ? 0 : n
        const next = Math.max(1, current + delta)
        setQuantities(q => ({ ...q, [key]: String(next) }))
    }

    function parsedQuantity(key) {
        const raw = quantities[key] ?? '0'
        const n = parseInt(raw, 10)
        return (isNaN(n) || n < 0) ? 0 : n
    }

    function getSelectedVariantId(group) {
        const selected = selectedVariant[group.key]
        if (selected) {
            if (group.variants.find(v => v.id === selected)) return selected
        }
        const sorted = [...group.variants].sort((a, b) =>
            naturalYearSort(a.year || '', b.year || '')
        )
        return sorted[0]?.id ?? null
    }

    function setSelectedVariantId(groupKey, inventoryId) {
        setSelectedVariant(s => ({ ...s, [groupKey]: inventoryId }))
    }

    async function handleAdd(group, variant) {
        if (!variant) return
        await addToCart(variant, parsedQuantity(group.key), () => {
            setQuantities(q => ({ ...q, [group.key]: '0' }))
        })
    }

    async function addToCart(variant, qty, onSuccess) {
        if (qty < 1) {
            toast.error('Please set a quantity before adding to cart')
            return
        }
        setAddingId(variant.id)
        try {
            const response = await cartApi.add(variant.id, qty)
            if (response.success) {
                const short = variant.description.length > 30
                    ? variant.description.slice(0, 30) + '…'
                    : variant.description
                toast.success(
                    variant.quantity === 0
                        ? `Backordered ${qty} × ${short}`
                        : `Added ${qty} × ${short}`
                )
                onSuccess?.()
                window.dispatchEvent(new Event('ghasc:cart-changed'))
            } else {
                toast.error(response.message || 'Could not add to cart')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setAddingId(null)
        }
    }

    function registerCardRef(key, el) {
        if (el) cardRefs.current[key] = el
        else delete cardRefs.current[key]
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="text-2xl font-semibold text-ghasc-navy">Shop</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Browse the catalog and add items to your cart. Visit your{' '}
                        <Link to="/cart" className="text-ghasc-navy hover:underline">cart</Link>{' '}
                        to review and check out.
                    </p>
                </div>
            </div>

            {/* Filters bar */}
            <div className="bg-white rounded-xl shadow-sm p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                <div className="sm:col-span-2 lg:col-span-2">
                    <Input
                        id="shop-search"
                        label="Search"
                        placeholder="Item number or description..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    >
                        <option value="">All categories</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div className="flex items-center justify-between gap-3">
                    <label className="flex items-center text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={filterNewOnly}
                            onChange={(e) => setFilterNewOnly(e.target.checked)}
                            className="mr-2"
                        />
                        New items only
                    </label>
                    {(searchText || filterCategory || filterNewOnly) && (
                        <button
                            onClick={() => { setSearchText(''); setFilterCategory(''); setFilterNewOnly(false) }}
                            className="text-sm text-ghasc-navy hover:underline"
                        >
                            Clear
                        </button>
                    )}
                </div>
            </div>

            {/* Catalog grid */}
            {loading ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
                    Loading...
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
                    {searchText || filterCategory || filterNewOnly
                        ? 'No items match those filters.'
                        : 'No items in the catalog yet.'}
                </div>
            ) : (
                <div className="space-y-6">
                    {sortedCategoryNames.map((category) => {
                        const useCleanTimeSort = category === 'KeyTags' || category === 'Chips'

                        const groups = Object.values(groupedByCategory[category])
                            .sort((a, b) => {
                                if (useCleanTimeSort) {
                                    const aYear = a.variants[0]?.year || ''
                                    const bYear = b.variants[0]?.year || ''
                                    return naturalYearSort(aYear, bYear)
                                }
                                const aKey = a.type === 'medallion'
                                    ? a.variants.map(v => v.itemNumber).sort(naturalItemSort)[0] || ''
                                    : a.variants[0]?.itemNumber || ''
                                const bKey = b.type === 'medallion'
                                    ? b.variants.map(v => v.itemNumber).sort(naturalItemSort)[0] || ''
                                    : b.variants[0]?.itemNumber || ''
                                return naturalItemSort(aKey, bKey)
                            })
                        return (
                            <section key={category}>
                                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 pb-1 border-b border-gray-200">
                                    {category}
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {groups.map((group) => {
                                        const isHighlighted = highlightedKey === group.key
                                        return group.type === 'medallion' ? (
                                            <MedallionCard
                                                key={group.key}
                                                cardRef={(el) => registerCardRef(group.key, el)}
                                                isHighlighted={isHighlighted}
                                                group={group}
                                                selectedVariantId={getSelectedVariantId(group)}
                                                onVariantChange={(id) => setSelectedVariantId(group.key, id)}
                                                quantity={getQuantity(group.key)}
                                                onQuantityChange={(v) => setQuantity(group.key, v)}
                                                onQuantityBlur={() => commitQuantity(group.key)}
                                                onQuantityStep={(delta) => stepQuantity(group.key, delta)}
                                                onAdd={(variant) => handleAdd(group, variant)}
                                                addingId={addingId}
                                            />
                                        ) : (
                                            <ItemCard
                                                key={group.key}
                                                cardRef={(el) => registerCardRef(group.key, el)}
                                                isHighlighted={isHighlighted}
                                                item={group.variants[0]}
                                                quantity={getQuantity(group.key)}
                                                onQuantityChange={(v) => setQuantity(group.key, v)}
                                                onQuantityBlur={() => commitQuantity(group.key)}
                                                onQuantityStep={(delta) => stepQuantity(group.key, delta)}
                                                onAdd={() => handleAdd(group, group.variants[0])}
                                                busy={addingId === group.variants[0].id}
                                                disabled={addingId !== null && addingId !== group.variants[0].id}
                                            />
                                        )
                                    })}
                                </div>
                            </section>
                        )
                    })}

                    <div className="text-xs text-gray-500 pt-2">
                        Showing {items.length} item{items.length === 1 ? '' : 's'}
                    </div>
                </div>
            )}
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Regular item card — one inventory row per card
// ---------------------------------------------------------------------------
function ItemCard({ item, quantity, onQuantityChange, onQuantityBlur, onQuantityStep, onAdd, busy, disabled, cardRef, isHighlighted }) {
    const variantLabel = [item.year, item.color].filter(Boolean).join(' / ')
    const stockState = item.quantity === 0 ? 'out' : item.quantity <= 3 ? 'low' : 'ok'

    const highlightClass = isHighlighted
        ? 'ring-2 ring-ghasc-tan ring-offset-2 transition-shadow duration-500'
        : ''

    return (
        <div
            ref={cardRef}
            className={`relative bg-white rounded-xl border border-gray-200 p-3 flex flex-col ${stockState === 'out' ? 'opacity-75' : ''} ${highlightClass}`}
        >
            {item.isNew && (
                <span className="absolute top-2 left-2 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-ghasc-tan/40 text-ghasc-navy z-10">
                    new
                </span>
            )}
            {item.isFeatured && (
                <span className="absolute top-2 right-2 text-base z-10" title="Featured item" aria-label="Featured">
                    ⭐
                </span>
            )}

            <div className="h-28 flex items-center justify-center bg-gray-50 rounded-lg mb-2.5">
                {item.imageUrl ? (
                    <img src={item.imageUrl} alt="" className="max-h-24 max-w-[80%] object-contain" />
                ) : (
                    <span className="text-xs text-gray-400">no image</span>
                )}
            </div>

            <div className="text-sm font-medium text-gray-900 leading-snug mb-1 min-h-[2.5em]">
                {item.description}
            </div>

            <div className="text-xs text-gray-500 font-mono mb-2">
                {item.itemNumber}
                {variantLabel && <span className="ml-2 font-sans">— {variantLabel}</span>}
            </div>

            <div className="flex items-baseline justify-between mb-2">
                <span className="text-lg font-semibold text-gray-900">
                    ${Number(item.cost).toFixed(2)}
                </span>
                <span className="text-xs">
                    <StockLabel state={stockState} qty={item.quantity} />
                </span>
            </div>

            <div className="flex items-center gap-1 mt-auto">
                <button
                    type="button"
                    onClick={() => onQuantityStep(-1)}
                    className="w-8 h-9 flex items-center justify-center bg-gray-100 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-200"
                    aria-label="Decrease quantity"
                >−</button>
                <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    value={quantity}
                    onChange={(e) => onQuantityChange(e.target.value)}
                    onBlur={onQuantityBlur}
                    className="no-spinner w-12 h-9 text-center border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                />
                <button
                    type="button"
                    onClick={() => onQuantityStep(1)}
                    className="w-8 h-9 flex items-center justify-center bg-gray-100 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-200"
                    aria-label="Increase quantity"
                >+</button>
                <Button
                    onClick={onAdd}
                    loading={busy}
                    disabled={disabled}
                    className="flex-1 ml-1"
                >
                    {stockState === 'out' ? 'Backorder' : 'Add'}
                </Button>
            </div>
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Medallion card — one card per medallion_type, with year (+color) selectors
// ---------------------------------------------------------------------------
function MedallionCard({ group, selectedVariantId, onVariantChange, quantity, onQuantityChange, onQuantityBlur, onQuantityStep, onAdd, addingId, cardRef, isHighlighted }) {
    const isTriplate = group.medallionType === 'Triplate'

    const years = useMemo(() => {
        const set = new Set(group.variants.map(v => v.year).filter(Boolean))
        return [...set].sort(naturalYearSort)
    }, [group.variants])

    const colors = useMemo(() => {
        const set = new Set(group.variants.map(v => v.color).filter(Boolean))
        return [...set].sort()
    }, [group.variants])

    const selected = group.variants.find(v => v.id === selectedVariantId) || group.variants[0]
    const selectedYear = selected?.year || ''
    const selectedColor = selected?.color || ''

    function handleYearChange(newYear) {
        const targetColor = isTriplate ? selectedColor : null
        const match = group.variants.find(v =>
            v.year === newYear &&
            (isTriplate ? v.color === targetColor : true)
        )
            || group.variants.find(v => v.year === newYear)
        if (match) onVariantChange(match.id)
    }

    function handleColorChange(newColor) {
        const match = group.variants.find(v =>
            v.color === newColor && v.year === selectedYear
        ) || group.variants.find(v => v.color === newColor)
        if (match) onVariantChange(match.id)
    }

    const cardImage = selected?.imageUrl || group.variants[0]?.imageUrl || null

    const stockState = !selected
        ? 'out'
        : selected.quantity === 0
            ? 'out'
            : selected.quantity <= 3 ? 'low' : 'ok'

    const busy = addingId === selected?.id
    const disabled = addingId !== null && addingId !== selected?.id

    const hasYearOptions = years.length > 0

    // Whether any variant in this medallion group is featured — if so, show the star
    const anyFeatured = group.variants.some(v => v.isFeatured)

    const highlightClass = isHighlighted
        ? 'ring-2 ring-ghasc-tan ring-offset-2 transition-shadow duration-500'
        : ''

    return (
        <div
            ref={cardRef}
            className={`relative bg-white rounded-xl border border-gray-200 p-3 flex flex-col ${stockState === 'out' ? 'opacity-75' : ''} ${highlightClass}`}
        >
            {selected?.isNew && (
                <span className="absolute top-2 left-2 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-ghasc-tan/40 text-ghasc-navy z-10">
                    new
                </span>
            )}
            {anyFeatured && (
                <span className="absolute top-2 right-2 text-base z-10" title="Includes featured items" aria-label="Featured">
                    ⭐
                </span>
            )}

            <div className="h-28 flex items-center justify-center bg-gray-50 rounded-lg mb-2.5">
                {cardImage ? (
                    <img src={cardImage} alt="" className="max-h-24 max-w-[80%] object-contain" />
                ) : (
                    <span className="text-xs text-gray-400">no image</span>
                )}
            </div>

            <div className="text-sm font-medium text-gray-900 leading-snug mb-1 min-h-[2.5em]">
                {group.medallionType} {hasYearOptions ? 'Medallion' : ''}
            </div>

            {hasYearOptions && (
                <div className="mb-1.5">
                    <label className="block text-xs text-gray-600 mb-0.5">Year</label>
                    <select
                        value={selectedYear}
                        onChange={(e) => handleYearChange(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    >
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            )}

            {isTriplate && colors.length > 0 && (
                <div className="mb-1.5">
                    <label className="block text-xs text-gray-600 mb-0.5">Color</label>
                    <select
                        value={selectedColor}
                        onChange={(e) => handleColorChange(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    >
                        {colors.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
            )}

            {!hasYearOptions && (
                <div className="text-xs text-gray-500 mb-1.5">
                    {selected?.description}
                </div>
            )}

            <div className="text-xs text-gray-500 font-mono mb-2">
                {selected?.itemNumber || '—'}
            </div>

            <div className="flex items-baseline justify-between mb-2">
                <span className="text-lg font-semibold text-gray-900">
                    ${Number(selected?.cost || 0).toFixed(2)}
                </span>
                <span className="text-xs">
                    <StockLabel state={stockState} qty={selected?.quantity || 0} />
                </span>
            </div>

            <div className="flex items-center gap-1 mt-auto">
                <button
                    type="button"
                    onClick={() => onQuantityStep(-1)}
                    className="w-8 h-9 flex items-center justify-center bg-gray-100 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-200"
                    aria-label="Decrease quantity"
                >−</button>
                <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    value={quantity}
                    onChange={(e) => onQuantityChange(e.target.value)}
                    onBlur={onQuantityBlur}
                    className="no-spinner w-12 h-9 text-center border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                />
                <button
                    type="button"
                    onClick={() => onQuantityStep(1)}
                    className="w-8 h-9 flex items-center justify-center bg-gray-100 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-200"
                    aria-label="Increase quantity"
                >+</button>
                <Button
                    onClick={() => onAdd(selected)}
                    loading={busy}
                    disabled={disabled || !selected}
                    className="flex-1 ml-1"
                >
                    {stockState === 'out' ? 'Backorder' : 'Add'}
                </Button>
            </div>
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------
function StockLabel({ state, qty }) {
    if (state === 'out') return <span className="text-ghasc-danger">Out of stock</span>
    if (state === 'low') return <span className="text-amber-700">Low: {qty}</span>
    return <span className="text-ghasc-success">{qty} in stock</span>
}

function naturalItemSort(a, b) {
    if (!a) return 1
    if (!b) return -1
    const ma = /^(\d+)(.*)$/.exec(a)
    const mb = /^(\d+)(.*)$/.exec(b)
    if (ma && mb) {
        const na = parseInt(ma[1], 10)
        const nb = parseInt(mb[1], 10)
        if (na !== nb) return na - nb
        return ma[2].localeCompare(mb[2])
    }
    return a.localeCompare(b)
}

function naturalYearSort(a, b) {
    const parseLabel = (s) => {
        if (!s) return [Number.MAX_SAFE_INTEGER, '']
        const lower = s.trim().toLowerCase()

        if (/^welcome/.test(lower)) return [0, s]
        if (/^multi(ple)?/.test(lower)) return [9_000_000, s]

        const m = /^(\d+)\s*(day|month|year)s?/i.exec(s)
        if (m) {
            const num = parseInt(m[1], 10)
            const unit = m[2].toLowerCase()
            if (unit === 'day') return [num, s]
            if (unit === 'month') return [num * 30, s]
            if (unit === 'year') return [num * 365, s]
        }

        return [9_500_000, s]
    }
    const [an, as] = parseLabel(a)
    const [bn, bs] = parseLabel(b)
    if (an !== bn) return an - bn
    return as.localeCompare(bs)
}